// ============================================
// Application Constants - Type-Safe Configuration
// ============================================

// Environment variable access with type safety
const getEnvVar = (key: string, fallback: string = ''): string => {
    return process.env[key] || fallback;
};

// API Configuration
export const TMDB_API_KEY = getEnvVar('REACT_APP_TMDB_API_KEY');
export const TMDB_BASE_URL = 'https://api.themoviedb.org/3' as const;
export const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p' as const;
export const VIDEO_SOURCE_URL = getEnvVar('REACT_APP_SRC_URL');

// Image sizes for different use cases
export const IMAGE_SIZES = {
    poster: {
        small: `${TMDB_IMAGE_BASE}/w185`,
        medium: `${TMDB_IMAGE_BASE}/w342`,
        large: `${TMDB_IMAGE_BASE}/w500`,
        original: `${TMDB_IMAGE_BASE}/original`,
    },
    backdrop: {
        small: `${TMDB_IMAGE_BASE}/w300`,
        medium: `${TMDB_IMAGE_BASE}/w780`,
        large: `${TMDB_IMAGE_BASE}/w1280`,
        original: `${TMDB_IMAGE_BASE}/original`,
    },
    profile: {
        small: `${TMDB_IMAGE_BASE}/w45`,
        medium: `${TMDB_IMAGE_BASE}/w185`,
        large: `${TMDB_IMAGE_BASE}/h632`,
        original: `${TMDB_IMAGE_BASE}/original`,
    },
} as const;

// YouTube Configuration
export const YOUTUBE_CONFIG = {
    musicChannel: getEnvVar('REACT_APP_MUSIC_CHANNEL', '@Black_Alpha_Productions'),
    podcastChannel: getEnvVar('REACT_APP_PODCAST_CHANNEL', '@thegoodcompanyke'),
    // Channel IDs for RSS feed (more reliable than scraping)
    musicChannelId: getEnvVar('REACT_APP_MUSIC_CHANNEL_ID', 'UC7wJQYfHv4SRtcBAhKy2lcw'),
    podcastChannelId: getEnvVar('REACT_APP_PODCAST_CHANNEL_ID', 'UC7h4tUtdH0L06sDZVmBMc4Q'),
    rssFeedBase: 'https://www.youtube.com/feeds/videos.xml?channel_id=',
    embedBase: 'https://www.youtube.com/embed/',
    thumbnailBase: 'https://img.youtube.com/vi/',
} as const;

// TV Stream Configuration
export const TV_STREAM_BASE = getEnvVar('REACT_APP_TV_STREAM_URL', 'https://daddylives.top/embed');

// App Configuration
export const APP_CONFIG = {
    name: 'BitePlay',
    searchDebounce: 300,
    sliderInterval: 6000,
    sliderCount: 5,
    castPerPage: 6,
    connectionTestTimeout: 500,
} as const;

// Breakpoints for responsive design
export const BREAKPOINTS = {
    mobile: 480,
    tablet: 768,
    laptop: 1024,
    desktop: 1280,
    tv: 1920,
    tv4k: 3840,
} as const;

// Navigation links interface
export interface NavLink {
    path: string;
    label: string;
    icon: string;
}

// Navigation links
export const NAV_LINKS: readonly NavLink[] = [
    { path: '/', label: 'Movies', icon: 'faFilm' },
    { path: '/tv', label: 'TV', icon: 'faTv' },
    { path: '/music', label: 'Music', icon: 'faMusic' },
    { path: '/podcasts', label: 'Podcasts', icon: 'faPodcast' },
] as const;

// Color gradients for cards
export const GRADIENTS: readonly string[] = [
    'from-violet-600 via-purple-600 to-indigo-600',
    'from-rose-600 via-pink-600 to-fuchsia-600',
    'from-amber-500 via-orange-500 to-red-500',
    'from-emerald-500 via-teal-500 to-cyan-500',
    'from-blue-600 via-indigo-600 to-purple-600',
    'from-pink-500 via-rose-500 to-orange-500',
    'from-cyan-500 via-blue-500 to-indigo-500',
    'from-lime-500 via-emerald-500 to-teal-500',
    'from-fuchsia-500 via-purple-500 to-violet-500',
    'from-yellow-500 via-amber-500 to-orange-500',
    'from-sky-500 via-blue-500 to-indigo-500',
    'from-red-500 via-rose-500 to-pink-500',
] as const;

// Type exports for inference
export type ImageSize = keyof (typeof IMAGE_SIZES)['poster'];
export type ImageType = keyof typeof IMAGE_SIZES;
export type Breakpoint = keyof typeof BREAKPOINTS;
